// TESSERAX — SÉQUENCE DE DÉMARRAGE (vitrine d'accueil, liens ?defi/?replay/?world, préférences
// visuelles persistées, listeners d'accueil, intro « wow », service worker).
// Extraits de main.ts (phase 6 du plan docs/main-split-plan.md), iso-comportement.
//
// boot(...) s'exécute UNE fois, après le câblage des modules par main.ts : il pose la
// formation vitrine dans le puits, résout les paramètres d'URL (défi daté, rejeu partagé,
// monde), synchronise l'accueil (profil, badges, classements) et arme l'intro.

import * as THREE from 'three';
import { GRID, HEIGHT, MAX_RESETS, LINES_PER_LEVEL } from '../core/constants';
import { gravityMs, lockDelayMs, firstRunAssist } from '../core/difficulty';
import { modeComplete, modeProgress } from '../core/modes';
import { strataCount, stratumMet } from '../core/strates';
import { recordAscent, saveMeta } from '../core/meta';
import { shouldSample } from '../core/pace';
import { showcaseFormation } from '../core/showcase';
import { attractScript } from '../core/attract';
import { createSim } from '../core/sim';
import { occupiedCells } from '../core/grid';
import { CODE as RC, decodeReplay, validateReplay } from '../core/replay';
import { todaySeed } from '../core/rng';
import { t, onLangChange } from '../i18n';
import * as net from '../net/api';
import { state } from './state';
import { el, setTxt, nfmt, IS_TOUCH, defiParam, activeDailySeed, fmtSeed, foldMode, maxColHeight, holeCount, hintSeen, markHint, onbMark, shake } from './util';
import type { SceneApi } from './scene';
import type { AudioApi } from './audio';
import type { FxApi } from './fx';
import type { RenderApi } from './render';
import type { HudApi } from './hud';
import type { InputApi } from './input';
import type { ReplayApi } from './replay';
import type { MenusApi } from './menus';
import type { FlowApi } from './flow';
import type { RulesApi } from './rules';

export function boot(viz: SceneApi, audio: AudioApi, fx: FxApi, render: RenderApi, hud: HudApi, input: InputApi, replayMod: ReplayApi, menus: MenusApi, flow: FlowApi, rules: RulesApi, loop: { resize(): void; frameCamera(): number }) {
  const V=viz as any,A=audio as any,F=fx as any,R=render as any,H=hud as any,I=input as any,REP=replayMod as any,M=menus as any,FL=flow as any,RU=rules as any;
  const { syncPileCells, cellWorld, applyQuality } = V;
  const { clearPops } = F;
  const { initAudio, menuAmbience } = A;
  const { updateMinimap, updateCam, updatePreview, updateHold } = R;
  const { updateHUD, renderMissions, updateSignal, updateSerie, updateInstab } = H;
  const { renderWorld } = M as any;
  const { startReplay, exitReplayQuiet } = REP;
  const { loadStartLB, renderProfile, updateStreakBadge, updateModeBests, updateAchLink, updateRelicLink,
    updateStartNext, updateTutHint, updateDailyAbout, updateChallengeCount, renderChallenges, renderArchMini, updateSessionRecap } = M;
  const { loadBest, startTutorial, launchMode, armConfirm } = FL;
  const { setWorld } = RU;
  const { resize } = loop;
  // Vitrine d'accueil : un puits NON vide derrière la caméra qui orbite → effet
  // « wow » immédiat (on voit du jeu, pas une grille déserte). Purement visuel
  // (aucune mutation de `grid`) ; effacé dès qu'une partie démarre.
  // Formation « Crystal Resonance » de la vitrine : générée chaque JOUR depuis la
  // graine du défi (showcaseFormation, cœur pur testé) → l'écran d'accueil n'est plus
  // figé mais un artefact qui se renouvelle (DA : civilisation supérieure). Purement
  // visuel, hors-partie. La couleur reste décidée par la hauteur (palette DA :
  // base bleu nuit → violet → cyan cristal → couronne ambrée).
  function showcaseColFor(y){
    if(y>=8)return 0xffd42b;  // couronne ambrée
    if(y>=5)return 0x43d9ff;  // cyan cristal
    if(y>=2)return 0xa855f7;  // violet médian
    return 0x1a3a7a;}          // bleu nuit (socle)
  function showcaseWell(){const cells=showcaseFormation(activeDailySeed()),out=[];
    for(let i=0;i<cells.length;i++){const c=cells[i];out.push([c[0],c[1],c[2],showcaseColFor(c[1])]);}
    clearPops();syncPileCells(out);}
  // ===== MODE ATTRACT (ROADMAP §1 « wow en 10 s ») : derrière le menu, le puits JOUE. =====
  // Un journal déterministe généré par le bot (core/attract, graine du jour → se renouvelle
  // chaque jour comme la formation qu'il remplace) est rejoué dans le sim headless (createSim,
  // le MÊME moteur que l'anti-triche) et rendu via syncPileCells — purement visuel : aucune
  // mutation de l'état de jeu, de la RNG de run ni du méta. Muet dès qu'une partie / un rejeu
  // reprend la main (le tick ne touche plus la pile) ; reboucle au retour à l'accueil.
  // Repli gracieux : reduced-motion, échec de génération ou erreur au tick → vitrine STATIQUE.
  let atkScript=null,atkSim=null,atkT0=0,atkDur=0,atkOn=false,atkSeed=0,atkTimer=null;
  function menuIdle(){const so=el('startOv');return !state.running&&!!so&&!so.classList.contains('hide');}
  function attractTick(){try{
      if(!menuIdle()){if(atkOn){atkOn=false;atkSim=null;}return;}       // partie/rejeu en cours → muet
      const now=performance.now();
      if(!atkOn||now-atkT0>=atkDur){atkOn=true;atkT0=now;atkSim=createSim(atkSeed,'free',atkScript.slice());}
      atkSim.stepTo(now-atkT0);
      const S=atkSim.state,out=occupiedCells(S.grid),c=S.current;
      if(c)for(let i=0;i<c.cells.length;i++){const q=c.cells[i],x=q[0]+c.pos[0],y=q[1]+c.pos[1],z=q[2]+c.pos[2];
        if(x>=0&&x<GRID&&y>=0&&y<HEIGHT&&z>=0&&z<GRID)out.push([x,y,z,c.color]);}
      clearPops();syncPileCells(out);
    }catch(e){atkScript=null;if(atkTimer){clearInterval(atkTimer);atkTimer=null;}try{clearPops();showcaseWell();}catch(_e){}}}
  function attractInit(){try{
      const rm=window.matchMedia&&window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if(rm)return;                                                     // mouvement réduit → vitrine statique
      atkSeed=(parseInt(activeDailySeed(),10)>>>0)||1;
      atkScript=attractScript(atkSeed);
      if(atkScript&&atkScript.length){atkDur=atkScript[atkScript.length-1][0]+1600;atkTimer=setInterval(attractTick,90);}else atkScript=null;
    }catch(e){atkScript=null;}}
  updateMinimap();updateCam();resize();loadBest();renderProfile();updateStreakBadge();updateModeBests();updateAchLink();updateRelicLink();updateStartNext();updateTutHint();
  // RET-METRICS-FUNNEL-001 : retour un NOUVEAU jour calendaire (proxy rétention J1/J7). Best-effort, privé.
  try{const _td=todaySeed();const _ls=localStorage.getItem('tesserax:lastSeen')||'';if(_ls&&_ls!==_td)net.track('return_day');if(_ls!==_td)localStorage.setItem('tesserax:lastSeen',_td);}catch(e){}
  // Chambre cosmétique équipée (boutique) : aperçu sur la vitrine d'accueil dès le chargement.
  try{if(state.worldPref!=='auto')setWorld(+state.worldPref,false);}catch(e){}
  showcaseWell();   // posée d'abord (contenu immédiat) ; la démo attract prend le relais au 1er tick
  attractInit();
  // Recadrage aux transitions accueil↔jeu : l'offset « attract » de frameCamera dépend de la
  // visibilité de startOv → un resize coalescé PAR TRANSITION (jamais par frame). Sans
  // MutationObserver (très vieux navigateur) : l'offset s'applique au prochain resize naturel.
  try{const so=el('startOv');if(so&&window.MutationObserver)new MutationObserver(()=>resize()).observe(so,{attributes:true,attributeFilter:['class']});}catch(e){}
  // Surcharge de monde pour tests/partage : ?world=N (0..5). Verrouille le monde
  // pour la session (worldPref) afin qu'il survive au démarrage d'une partie.
  try{const wq=new URLSearchParams(location.search).get('world');if(wq!=null&&/^[0-5]$/.test(wq)){state.worldPref=wq;setWorld(+wq,false);}}catch(e){}
  // Lien de REJEU partageable : ?replay=<journal encodé>&rs=<score>&rn=<nom> → lecture directe.
  // Décodage TOLÉRANT + validation stricte (cf. core/replay) → lien mort/forgé = repli silencieux.
  try{const _params=new URLSearchParams(location.search),_rp=_params.get('replay');
    if(_rp){const _obj=decodeReplay(_rp);if(_obj&&validateReplay(_obj)){
      const _rsc=parseInt(_params.get('rs')||'0',10)||0,_rn=(_params.get('rn')||'').slice(0,12);
      startReplay(_obj,_rsc,_rn);}}
    // Lien de REJEU COURT : /r/<id> (rebond → ?r=<id>) → on va chercher le journal stocké (D1) puis on
    // lance la lecture. ASYNCHRONE (un aller-retour réseau) ; mêmes décodage tolérant + validation
    // stricte. Indispo (id mort, hors-ligne, table absente) → repli silencieux sur l'accueil.
    // L'id COURT fait TOUJOURS 10 caractères [a-z0-9] (functions/api/replay.genId). Certaines cibles de
    // partage COLLENT le texte de partage juste après l'URL (« …/r/<id> Watch my TESSERAX ◆ ») → le lien
    // ouvert peut porter un `?r=<id><texte mutilé>`. On AUTO-RÉPARE en ne gardant que les 10 premiers
    // caractères alphanumériques (= l'id canonique) au lieu d'interroger la chaîne polluée (qui ne
    // correspond à rien → « pas de rejeu »). Un id propre de 10 car. est inchangé.
    else{const _id=(_params.get('r')||'').replace(/[^A-Za-z0-9]/g,'').slice(0,10);
      if(_id)net.fetchSharedReplay(_id).then(d=>{if(!d)return;const _obj=decodeReplay(d.replay);
        if(_obj&&validateReplay(_obj))startReplay(_obj,d.score,d.name);});}}catch(e){}
  // Préférence de son persistée (le choix « muet » se souvient d'une session à l'autre). `muted` est déjà chargé
  // avec les autres prefs (voir bloc plus haut) ; ici on ne fait QUE synchroniser les glyphes une fois le DOM prêt.
  try{if(state.muted){const mb=el('muteBtn');if(mb)mb.classList.add('muted');const ps=el('pauseSound');if(ps)ps.classList.add('muted');}}catch(e){}
  {const _n=net.getName();if(_n&&_n!=='Anon')el('nameInput').value=_n;}
  // UIUX-021 : au focus du champ pseudo, le ramener au centre du viewport pour qu'il ne soit
  // pas masqué par le clavier mobile (et que le bouton JOUER juste dessous reste atteignable).
  {const _ni=el('nameInput');if(_ni)_ni.addEventListener('focus',()=>{try{setTimeout(()=>_ni.scrollIntoView({block:'center',behavior:'smooth'}),250);}catch(e){}});}
  // Écran d'accueil : top du jour OU records éternels (onglets), + vie mondiale.
  updateSignal();updateSerie();updateInstab();renderArchMini();state.rightReady=true;
  setInterval(updateSignal,1000);
  try{net.flushPendingScores();}catch(e){}   // ROB-NET-002 : rejoue les scores mis en file hors-ligne (best-effort)
  // Changement de langue en direct : on re-rend les éléments dynamiques.
  onLangChange(()=>{[renderMissions,updateAchLink,renderProfile,updateStreakBadge,updateModeBests,updateDailyAbout,updateChallengeCount,loadStartLB,renderArchMini,updateHUD,updateTutHint].forEach(fn=>{try{fn();}catch(e){}});});
  // Le panneau « Signal quotidien » EST le défi du jour : clic → lance le défi (hors partie).
  {const sp=el('signalPanel');if(sp)sp.addEventListener('click',()=>{if(!state.running){const b=el('playDaily');if(b)b.click();}});}
  // ===== Intro « wow » : skippable au tap/clavier ; auto-révèle après la séquence =====
  const introOv=el('introOv');
  function dismissIntro(e){if(!introOv||introOv.classList.contains('gone'))return;introOv.classList.add('gone');initAudio();
    // Tactile : le tap qui ferme le splash synthétise un « click » fantôme qui, l'overlay
    // devenu transparent aux pointeurs, TRAVERSERAIT vers le bouton de menu dessous. On
    // avale ce premier click (une seule fois, fenêtre courte) — fini le menu cliqué par mégarde.
    if(e&&e.type==='pointerdown'){const swallow=ev=>{ev.stopPropagation();ev.preventDefault();};
      window.addEventListener('click',swallow,{capture:true,once:true});
      setTimeout(()=>window.removeEventListener('click',swallow,{capture:true}),700);}
    setTimeout(()=>{introOv.style.display='none';},650);}
  if(introOv){
    if(navigator.webdriver){introOv.style.display='none';}            // pas d'intro sous automation (E2E)
    else{introOv.addEventListener('pointerdown',dismissIntro,{once:true});
      window.addEventListener('keydown',dismissIntro,{once:true});
      const rm=window.matchMedia&&window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      setTimeout(dismissIntro,rm?500:4400);}
  }
  net.fetchStats().then(st=>renderWorld(el('worldStart'),st));
  // Profil joueur D1 (rang mondial + rareté des succès). Best-effort, asynchrone :
  // dès qu'il arrive on rafraîchit le profil d'accueil (le panneau Succès lit le
  // cache à l'ouverture). Échec silencieux en dev/hors-ligne.
  try{net.fetchProfile(net.getPid()).then(p=>{if(p){state.profileData=p;renderProfile();}});}catch(e){}
  // Lien de défi partagé (?defi=YYYYMMDD) : on met en avant le bouton et on
  // précise la date du défi quand elle diffère du jour courant.
  try{const defi=defiParam();if(defi){const b=el('playDaily');if(b){
    const lbl=b.querySelector('.mk-lbl')||b;  // touche machinée : ne réécrire QUE le libellé (préserve icône + LED)
    lbl.textContent=defi===todaySeed()?t('start.takeDaily',undefined,'RELEVER LE DÉFI DU JOUR'):t('start.takeDailyDate',{date:fmtSeed(defi)},'RELEVER LE DÉFI DU '+fmtSeed(defi));
    b.style.boxShadow='0 0 0 2px #ff6fd8, 0 0 22px rgba(255,111,216,.6)';}}}catch(e){}
  // Service worker : jeu hors-ligne + cache « qualité installable » (PWA). Production
  // uniquement, et jamais sous automation E2E (navigator.webdriver) pour ne pas perturber
  // les tests. Échec silencieux — la règle de dégradation gracieuse s'applique.
  try{if('serviceWorker' in navigator&&import.meta.env&&import.meta.env.PROD&&!navigator.webdriver){
    window.addEventListener('load',()=>{navigator.serviceWorker.register('/sw.js').catch(()=>{});});}}catch(e){}
}
